import { Component, EventEmitter, Input, OnDestroy, Output, signal } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { DesignChatFailure, DesignChatTurn, DesignChatViewing, EndClientReport } from '../../../models/end-client-reporting-package';
import { EndClientReportService } from '../end-client-report.service';

/** One line in the transcript. `error` is ours, not the agent's -- see failureMessage(). */
interface ChatEntry {
  role: 'user' | 'agent' | 'error';
  text: string;
}

@Component({
  selector: 'or-report-chat',
  templateUrl: './report-chat.component.html',
  styleUrls: ['./report-chat.component.scss'],
  standalone: false
})
export class ReportChatComponent implements OnDestroy {
  /** The deck being edited. */
  @Input({ required: true }) reportId!: string;

  /**
   * The reportDesignVersion currently loaded in the browser. Sent with every turn so an instruction
   * aimed at a deck that has since been replaced is refused rather than applied to a document the
   * user can't see.
   */
  @Input({ required: true }) baseVersion!: number;

  /**
   * The subsection and page on screen, sent with each turn so "this page" and "here" mean what the
   * user is looking at. Read when the message is sent, so it follows them around the deck.
   */
  @Input() viewing: DesignChatViewing | null = null;

  /** Set while manual subsection editing is on: both would be writing the same document. */
  @Input() disabled = false;

  /**
   * Its own heading and close button. Off where the chat is drawn inside a window that has a bar of
   * its own carrying both -- the deck's floating chat window.
   */
  @Input() showHeader = true;

  /**
   * Emitted only when the design actually changed, carrying the report as it now stands. The parent
   * owns reloading the design and holding the new version -- this component never mutates either.
   */
  @Output() designChanged = new EventEmitter<EndClientReport>();

  /**
   * Asked to close. The panel is opened from a menu that only shows a check mark, so it needs a way
   * out of its own -- the parent owns whether it's open, so this reports rather than decides.
   */
  @Output() closed = new EventEmitter<void>();

  entries = signal<ChatEntry[]>([]);
  draft = signal('');
  sending = signal(false);

  // Null until the first reply comes back with the id the server minted. Kept here rather than on
  // the parent because it identifies this conversation, not this deck -- closing the panel and
  // reopening it deliberately continues where it left off, while a reload starts fresh.
  private sessionId: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(private reportService: EndClientReportService) { }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  send(): void {
    const message = this.draft().trim();
    if (!message || this.sending() || this.disabled) return;

    this.entries.update(entries => [...entries, { role: 'user', text: message }]);
    this.draft.set('');
    this.sending.set(true);

    this.reportService.sendDesignChat(this.reportId, message, this.sessionId, this.baseVersion, this.viewing)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (turn: DesignChatTurn) => {
          this.sessionId = turn.sessionId;
          this.entries.update(entries => [...entries, { role: 'agent', text: turn.message }]);
          this.sending.set(false);
          // Only when something actually changed: an answered question or a clarifying request
          // comes back through this same call, and reloading the deck for those would be noise.
          if (turn.designChanged && turn.report) this.designChanged.emit(turn.report);
        },
        error: (failure: DesignChatFailure) => {
          this.entries.update(entries => [...entries, { role: 'error', text: failureMessage(failure) }]);
          this.sending.set(false);
        }
      });
  }
}

// Written for the person in the chat panel, not for a log: each one says whether anything changed
// (nothing did, in every case here) and what to do next.
function failureMessage(failure: DesignChatFailure): string {
  switch (failure) {
    case 'Rejected':
      return `That edit couldn't be applied safely, so nothing changed. Try describing it a different way — naming the page or chart usually helps.`;
    case 'Busy':
      return `The deck was being saved just then, so nothing changed. Try again in a moment.`;
    case 'VersionConflict':
      return `This deck changed since you opened it, so nothing was edited. Reload it to catch up first.`;
    case 'ArchiveFailed':
      return `The previous version of this deck couldn't be filed away, so the edit was abandoned rather than overwrite it. Nothing changed.`;
    default:
      return `The assistant couldn't be reached, so nothing changed. Try again in a moment.`;
  }
}
